import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  SimpleGrid,
  Heading,
  Text,
  useColorModeValue,
  SlideFade,
  Card,
  CardBody,
  Container,
  Icon,
  VStack,
} from '@chakra-ui/react';
import {
  HiOutlineGlobeAlt,
  HiOutlineMap,
  HiOutlineLocationMarker,
  HiOutlineOfficeBuilding,
  HiOutlineHome as HiOutlineVillage,
} from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { useRegionData } from '../../../contexts/RegionDataContext';
import SubDivision from './components/SubDivision';
import TahsilSection from './components/TehsilSection';
import Mandal from './components/Mandal';
import SajjaSection from './components/SajjaSection';
import VillageSection from './components/VillageSection';

const RegionDataAdd = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);

  const { counts } = useRegionData();
  const { t } = useTranslation();

  const bgGradient = useColorModeValue(
    'linear-gradient(135deg, #f8fcfc 0%, #e9eef4 30%, #e3eff3 70%)',
    'linear-gradient(135deg, #2b3648 0%, #1e2a46 50%, #0c4a6e 100%)',
  );
  const cardbgGradient = useColorModeValue(
    'linear(to-br, #eafdfd, #bce2f9)',
    'linear(to-br, gray.900, #006280)',
  );
  const cardBg = useColorModeValue(
    'linear-gradient(135deg, #f8f3f3 0%, #eef3f9 30%, #f1f8fb 70%)',
    'gray.800',
  );
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('blue.800', 'white');
  const loaderBg = useColorModeValue('gray.50', 'gray.900');
  const loaderPattern = useColorModeValue(
    'radial-gradient(circle at 25px 25px, rgba(120, 119, 198, 0.3) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(120, 119, 198, 0.3) 2%, transparent 0%)',
    'radial-gradient(circle at 25px 25px, rgba(66, 153, 225, 0.15) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(66, 153, 225, 0.15) 2%, transparent 0%)',
  );
  const loaderGradient = useColorModeValue(
    'linear(to-r, blue.400, purple.500)',
    'linear(to-r, blue.500, purple.600)',
  );

  useEffect(() => {
    const loadUser = () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        setUser(userData);
      } catch (error) {
        setUser(null);
      } finally {
        setIsLoading(false);
        setTimeout(() => setShowContent(true), 300);
      }
    };

    setTimeout(loadUser, 100);
  }, []);

  if (isLoading) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={loaderBg}
        position="relative"
        overflow="hidden"
        borderRadius="20px"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundImage={loaderPattern}
          backgroundSize="100px 100px"
        />

        <Flex direction="column" align="center" gap={6}>
          <Box
            width="100px"
            height="100px"
            borderRadius="full"
            bgGradient={loaderGradient}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={HiOutlineGlobeAlt} boxSize={12} color="white" />
          </Box>
        </Flex>
      </Flex>
    );
  }

  if (!user) {
    return (
      <Container maxW="container.xl" py={20}>
        <Flex direction="column" align="center" spacing={6} textAlign="center">
          <Icon as={HiOutlineGlobeAlt} boxSize={16} color="gray.400" />
          <Heading size="lg" color={textColor} mb={2}>
            {t('authRequired') || 'Authentication Required'}
          </Heading>
          <Text color={subtitleColor}>
            {t('pleaseLogin') || 'Please log in to access region management'}
          </Text>
        </Flex>
      </Container>
    );
  }

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
      borderRadius="20px"
      pt={'100px'}
    >
      <Box
        position="absolute"
        top="-50%"
        left="-50%"
        width="200%"
        height="200%"
        background="radial-gradient(circle, rgba(66, 153, 225, 0.1) 1px, transparent 1px)"
        backgroundSize="50px 50px"
        opacity="0.1"
      />

      <Container maxW="7xl" mx="auto" pb={2}>
        <SlideFade in={showContent} offsetY="20px">
          <Flex
            direction="column"
            mb={3}
            p={3}
            borderRadius="2xl"
            boxShadow="0 10px 40px rgba(0, 0, 0, 0.05)"
            position="relative"
            overflow="hidden"
            gap={6}
            bgGradient={cardbgGradient}
          >
            <Box
              position="absolute"
              top="-50px"
              right="-50px"
              w="200px"
              h="200px"
              borderRadius="full"
              bgGradient="linear(to-br, blue.200, purple.200)"
              opacity={0.05}
            />
            <Box
              position="absolute"
              bottom="-30px"
              left="-30px"
              w="150px"
              h="150px"
              borderRadius="full"
              bgGradient="linear(to-br, teal.200, cyan.200)"
              opacity={0.05}
            />

            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
              <Flex align="center" gap={4}>
                <Flex
                  bgGradient="linear(to-br, #4B80DE, purple.600)"
                  p={3}
                  borderRadius="xl"
                  boxShadow="0 10px 30px rgba(66, 153, 225, 0.2)"
                >
                  <Icon as={HiOutlineGlobeAlt} boxSize={6} color="white" />
                </Flex>
                <VStack align="flex-start" spacing={1}>
                  <Text
                    as="h1"
                    fontSize="2xl"
                    fontWeight="bold"
                    color={headingColor}
                  >
                    {t('Region Data') || 'Region Data Management'}
                  </Text>
                  <Text color={subtitleColor} fontSize="md">
                    Manage geographical divisions and administrative units
                  </Text>
                </VStack>
              </Flex>
            </Flex>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} width="100%">
              {[
                {
                  label: 'Tahsils',
                  value: counts.tahsil.toString(),
                  color: 'blue',
                  icon: HiOutlineMap,
                },
                {
                  label: 'Mandals',
                  value: counts.mandal.toString(),
                  color: 'purple',
                  icon: HiOutlineOfficeBuilding,
                },
                {
                  label: 'Sajja',
                  value: counts.sajja.toString(),
                  color: 'green',
                  icon: HiOutlineLocationMarker,
                },
                {
                  label: 'Villages',
                  value: counts.village.toString(),
                  color: 'orange',
                  icon: HiOutlineVillage,
                },
              ].map((stat, index) => (
                <Card
                  key={index}
                  borderRadius="xl"
                  bg={cardBg}
                  boxShadow="0 10px 30px rgba(0,0,0,0.08)"
                  _hover={{
                    transform: 'translateY(-4px)',
                    transition: 'transform 0.2s',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardBody p={5}>
                    <Flex align="center" gap={3}>
                      <Box p={2} borderRadius="lg" bg={`${stat.color}.100`}>
                        <Icon
                          as={stat.icon}
                          boxSize={5}
                          color={`${stat.color}.500`}
                        />
                      </Box>
                      <VStack align="flex-start" spacing={0}>
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color={textColor}
                        >
                          {stat.value}
                        </Text>
                        <Text fontSize="sm" color={subtitleColor}>
                          {stat.label}
                        </Text>
                      </VStack>
                    </Flex>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Flex>

          <Box position="relative">
            <Grid templateColumns="1fr" gap={4}>
              <VStack spacing={0} align="stretch">
                <SubDivision />

                <TahsilSection />

                <Mandal />

                <SajjaSection />

                <VillageSection />
              </VStack>
            </Grid>
          </Box>
        </SlideFade>
      </Container>

      <Box position="fixed" bottom="30px" right="30px" zIndex={999}>
        <Box
          as="button"
          width="50px"
          height="50px"
          borderRadius="full"
          bg="blue.500"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 4px 15px rgba(66, 153, 225, 0.3)"
          _hover={{
            bg: 'blue.600',
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          transition="all 0.2s"
        >
          <Text fontSize="xl" color="white">
            ↑
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default RegionDataAdd;
